import { AxisType, Channel } from '@lmt/signal-viewer-ui';

export enum EventType {
  SoftStart = '241',
  TimedBreath = '221',
  MaskTest = '172',
  ALeckage = '103',
  AMovement = '106',
  HLeckage = '113',
  Artefact = '141',
  eFL = '3',
  eMO = '2',
  eSO = '1',
  LeakageAlert = '305',
  DisconnectionAlert = '307',
  oA = '101',
  oH = '111',
  uA = '108',
  CSRespiration = '181',
  cA = '102',
  cH = '112',
  HighPressure = '105',
  eS = '4',
  Snore = '131',
  RERA = '121',
  SpO2Low = '490',
  SpO2High = '491',
  SpO2SignalQualityPoorOrLow = '790',
  FiO2Low = '494',
  FiO2High = '495',
  CO2Low = '496',
  CO2High = '497',
  PressureLow = '321',
  PressureHigh = '322',
  PressurePermanentLow = '323',
  Occlusion = '757',
  PressureContinuouslyConstant = '758',
  BackwardBreathing = '796',
  PressureLimitExceeded = '811',
  PressureLimitReached = '825',
  Apnea = '311',
  FlowLow = '365',
  VolumePermanentLow = '756',
  MinuteVolumeLow = '309',
  MinuteVolumeHigh = '320',
  VolumeLow = '315',
  VolumeHigh = '316',
  FrequencyLow = '317',
  FrequencyHigh = '318',
  PulseLow = '492',
  PulseHigh = '493'
}

export enum ChannelType {
  Pressure = '1',
  RespFlow = '2',
  Obstruction = '3',
  rMV = '4',
  RespiratoryInstability = '5',
  Leak = '6',
  ObstructiveAH = '7',
  CentralAH = '8',
  Snore = '9',
  SpO2 = '10',
  FiO2 = '11',
  CO2 = '12',
  VolumeTrend = '13',
  RespCycles = '14',
  MinuteVolume = '15',
  TidalVolume = '16',
  RespRate = '17',
  TiT = '18',
  PartInspExp = '19',
  Ti_Te = '20',
  PulseRate = '21',
  PhaseTrigger = '22'
}

export const ReportChannels: Channel[] = [
  {
    id: ChannelType.Pressure,
    configuration: {
      name: 'Pressure',
      relativeHeight: 18,
      tickInterval: { start: 0, end: 25, step: 5 },
      axisType: AxisType.SignalsWithEvents,
    },
    signals: [
      {
        type: 'Pressure',
        config: {
          label: 'Pressure',
          color: '#454545',
          zoomRestriction: { max: 60 * 60 * 1000 },
        },
      },
      {
        type: 'IPAP',
        config: {
          label: 'IPAP',
          color: '#446CAB',
          zoomRestriction: { min: 60 * 1000 },
        },
      },
      {
        type: 'EPAP',
        config: {
          label: 'EPAP',
          color: '#D17D7D',
          zoomRestriction: { min: 60 * 1000 },
        },
      },
      {
        type: 'CPAPPressure',
        config: {
          label: '(auto)CPAP/EEPAP',
          color: '#8A4545',
          zoomRestriction: { min: 60 * 1000 },
        },
      },
    ],
    events: [
      {
        type: EventType.MaskTest,
        config: {
          label: 'Mask test',
          fillColor: '#D6E3BC',
          borderColor: '#000000',
          relativeHeight: 1,
        },
      },
      {
        type: EventType.SoftStart,
        config: {
          label: 'softSTART',
          fillColor: '#DCDCDC',
          borderColor: '#000000',
          relativeHeight: 1,
        },
      },
      {
        type: EventType.TimedBreath,
        config: {
          label: 'Timed breath',
          fillColor: '#8B0000',
          borderColor: '#8B0000',
          relativeHeight: 0.14,
        },
      },
    ],
  },
  {
    id: ChannelType.RespFlow,
    configuration: {
      name: 'Resp. Flow',
      relativeHeight: 16,
      tickInterval: { start: -100, end: 100, step: 50 },
      axisType: AxisType.SignalsWithEvents,
    },
    signals: [
      {
        type: 'RespFlow',
        config: { label: 'Resp. Flow', color: '#454545' },
      },
    ],
    events: [
      {
        type: EventType.ALeckage,
        config: {
          label: 'A leakage',
          fillColor: '#ffffff',
          borderColor: '#808080',
          relativeHeight: 0.9,
        },
      },
      {
        type: EventType.AMovement,
        config: {
          label: 'A movement',
          fillColor: '#ffffff',
          borderColor: '#808080',
          relativeHeight: 0.9,
        },
      },
      {
        type: EventType.HLeckage,
        config: {
          label: 'H leakage',
          fillColor: '#ffffff',
          borderColor: '#bfbfbf',
          relativeHeight: 0.9,
        },
      },
      {
        type: EventType.Artefact,
        config: {
          label: 'Artefact',
          fillColor: '#FFCC66',
          borderColor: '#000000',
          relativeHeight: 1,
        },
      },
    ],
  },
  {
    id: ChannelType.rMV,
    configuration: {
      name: 'rMV',
      relativeHeight: 10,
      tickInterval: { start: 0, end: 200, step: 100 },
      axisType: AxisType.SignalsWithEvents,
    },
    signals: [{ type: 'rRMV', config: { label: 'rel. MV', color: '#404040' } }],
    events: [
      {
        type: EventType.eFL,
        config: {
          label: 'eFL',
          fillColor: '#FFFFBB',
          borderColor: '#000000',
          relativeHeight: 0.1,
        },
      },
      {
        type: EventType.eMO,
        config: {
          label: 'eMO',
          fillColor: '#D17D7D',
          borderColor: '#111111',
          relativeHeight: 0.15,
        },
      },
      {
        type: EventType.eSO,
        config: {
          label: 'eSO',
          fillColor: '#8A4545',
          borderColor: '#111111',
          relativeHeight: 0.15,
        },
      },
    ],
  },
  {
    id: ChannelType.Obstruction,
    configuration: {
      name: 'Obstruction',
      relativeHeight: 10,
      tickInterval: { start: 0, end: 100, step: 20 },
      axisType: AxisType.SignalsWithEvents,
    },
    signals: [
      {
        type: 'ObstructLevel',
        config: { label: 'Obstruction Level', color: '#8b0000' },
      },
    ],
    events: [],
  },
  {
    id: ChannelType.RespiratoryInstability,
    configuration: {
      name: 'Respiratory instability',
      relativeHeight: 8,
      tickInterval: { start: 0, end: 10, step: 10 },
      axisType: AxisType.SignalsWithEvents,
    },
    signals: [
      {
        type: 'rMVFluctuation',
        config: { label: 'Respiratory instability', color: '#B200FF' },
      },
    ],
    events: [],
  },
  {
    id: ChannelType.Leak,
    configuration: {
      name: 'Unintentional leak',
      relativeHeight: 18,
      tickInterval: { start: 0, end: 75, step: 25 },
      marks: [
        { value: 50, color: '#B32400' },
        { value: 25, color: '#994C00' },
      ],
      axisType: AxisType.SignalsWithEvents,
    },
    signals: [
      {
        type: 'LeakFlowBreath',
        config: { label: 'Unintentional leak flow', color: '#000000' },
      },
    ],
    events: [
      {
        type: EventType.LeakageAlert,
        config: {
          label: 'Leakage alert',
          fillColor: '#CC6556',
          borderColor: '#000000',
          relativeHeight: 0.15,
        },
      },
      {
        type: EventType.DisconnectionAlert,
        config: {
          label: 'Disconn. alert',
          fillColor: '#8b0000',
          borderColor: '#000000',
          relativeHeight: 0.25,
        },
      },
    ],
  },
  {
    id: ChannelType.ObstructiveAH,
    configuration: {
      name: 'Obstructive A/H',
      relativeHeight: 5,
      axisType: AxisType.EventOnly,
    },
    signals: [],
    events: [
      {
        type: EventType.oA,
        config: {
          label: 'oA',
          fillColor: '#CC6556',
          borderColor: '#000000',
          relativeHeight: 1,
        },
      },
      {
        type: EventType.oH,
        config: {
          label: 'oH',
          fillColor: '#FFAFA3',
          borderColor: '#000000',
          relativeHeight: 0.6,
        },
      },
      {
        type: EventType.uA,
        config: {
          label: 'uA',
          fillColor: '#bfbfbf',
          borderColor: '#000000',
          relativeHeight: 0.8,
        },
      },
    ],
  },
  {
    id: ChannelType.CentralAH,
    configuration: {
      name: 'Central A/H',
      relativeHeight: 5,
      axisType: AxisType.EventOnly,
    },
    signals: [],
    events: [
      {
        type: EventType.CSRespiration,
        config: {
          label: 'CS respiration',
          fillColor: '#ffff00',
          borderColor: '#000000',
          relativeHeight: 0.8,
        },
      },
      {
        type: EventType.cA,
        config: {
          label: 'cA',
          fillColor: '#3311DD',
          borderColor: '#000000',
          relativeHeight: 1,
        },
      },
      {
        type: EventType.cH,
        config: {
          label: 'cH',
          fillColor: '#6677DD',
          borderColor: '#000000',
          relativeHeight: 0.6,
        },
      },
      {
        type: EventType.HighPressure,
        config: {
          label: 'A high pressure',
          fillColor: '#bfbfbf',
          borderColor: '#808080',
          relativeHeight: 0.8,
        },
      },
    ],
  },
  {
    id: ChannelType.Snore,
    configuration: {
      name: 'Snoring/RERA',
      relativeHeight: 5,
      axisType: AxisType.EventOnly,
    },
    signals: [],
    events: [
      {
        type: EventType.eS,
        config: {
          label: 'eS',
          fillColor: '#7DB56E',
          borderColor: '#000000',
          relativeHeight: 1,
        },
      },
      {
        type: EventType.Snore,
        config: {
          label: 'Snore',
          fillColor: '#d8e4bc',
          borderColor: '#000000',
          relativeHeight: 0.6,
        },
      },
      {
        type: EventType.RERA,
        config: {
          label: 'RERA',
          fillColor: '#E8C064',
          borderColor: '#000000',
          relativeHeight: 1,
        },
      },
    ],
  }
];